import { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { PageHeader, EmptyState } from "@/components/common";
import { ListTodo, Plus, Trash2, Sparkles, Loader2, Target, Save, Wand2, CheckCircle2, AlertTriangle, Mail, Clock } from "lucide-react";
import { toast } from "sonner";

function RoleDescription() {
  const { data } = useApi("/work/role-description");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setText(data.text || ""); }, [data]);
  const save = async () => {
    setSaving(true);
    try { await api.put("/work/role-description", { text }); toast.success("Rollbeskrivning sparad"); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <Card className="mb-5 space-y-3 p-4" data-testid="role-desc-card">
      <div className="flex items-center gap-2 text-sm font-semibold"><Target className="h-4 w-4 text-accent" />Mitt uppdrag / rollbeskrivning</div>
      <p className="text-xs text-muted-foreground">Beskriv ditt uppdrag och ansvar. AI:n använder detta (och din aktiva placering) i fokusanalysen för att se om du jobbar med rätt saker.</p>
      <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} data-testid="role-desc-input"
        placeholder="t.ex. Jag ansvarar för konsultens leverans hos kund X: kravanalys, utveckling och veckoavstämningar." />
      <Button onClick={save} disabled={saving} size="sm" data-testid="role-desc-save"><Save className="mr-1 h-4 w-4" />Spara</Button>
    </Card>
  );
}

function ImportFromText({ onAdded }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState(null);
  const [sel, setSel] = useState({});
  const [mailBusy, setMailBusy] = useState(false);
  const scan = async () => {
    setBusy(true); setFound(null);
    try {
      const r = await api.post("/work/extract", { text });
      setFound(r.data.tasks || []);
      setSel(Object.fromEntries((r.data.tasks || []).map((_, i) => [i, true])));
      if (!(r.data.tasks || []).length) toast.info("Hittade inga tydliga uppgifter i texten");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const scanMail = async () => {
    setMailBusy(true); setFound(null);
    try {
      const r = await api.post("/work/mail/scan");
      if (!r.data.connected) { toast.info("Outlook är inte kopplat ännu – aktiveras automatiskt när er IT kopplar Microsoft 365 med mejl-behörighet."); return; }
      setFound(r.data.tasks || []);
      setSel(Object.fromEntries((r.data.tasks || []).map((_, i) => [i, true])));
      toast.success(`Läste ${r.data.scanned} mejl – ${(r.data.tasks || []).length} uppgifter hittade`);
    } catch (e) { toast.error(apiErr(e)); } finally { setMailBusy(false); }
  };
  const addSelected = async () => {
    const titles = (found || []).filter((_, i) => sel[i]);
    if (!titles.length) return;
    try { await api.post("/work/tasks/bulk", { titles, source: "ai" }); toast.success(`${titles.length} uppgifter tillagda`); setText(""); setFound(null); onAdded(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="mb-5 space-y-3 p-4" data-testid="import-text-card">
      <div className="flex items-center gap-2 text-sm font-semibold"><Wand2 className="h-4 w-4 text-accent" />Skapa uppgifter ur mejl / anteckningar</div>
      <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} data-testid="import-text-input"
        placeholder="Klistra in ett mejl, chatt eller mötesanteckningar – AI:n plockar ut att-göra-punkterna." />
      <div className="flex flex-wrap gap-2">
        <Button onClick={scan} disabled={busy || !text.trim()} data-testid="import-scan-btn">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />}{busy ? "Analyserar…" : "Hitta uppgifter"}
        </Button>
        <Button variant="outline" onClick={scanMail} disabled={mailBusy} data-testid="import-mail-btn">
          {mailBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Mail className="mr-1 h-4 w-4" />}Läs mina Outlook-mejl
        </Button>
      </div>
      {found && found.length > 0 && (
        <div className="space-y-2 rounded-lg border p-3" data-testid="import-results">
          {found.map((tk, i) => (
            <label key={i} className="flex items-start gap-2 text-sm" data-testid={`import-task-${i}`}>
              <Checkbox checked={!!sel[i]} onCheckedChange={(v) => setSel((s) => ({ ...s, [i]: !!v }))} className="mt-0.5" />
              <span>{tk}</span>
            </label>
          ))}
          <Button size="sm" onClick={addSelected} data-testid="import-add-btn"><Plus className="mr-1 h-4 w-4" />Lägg till valda</Button>
        </div>
      )}
    </Card>
  );
}

function FocusAnalysis() {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/work/focus-analysis"); setRes(r.data); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const pct = res?.on_mission_pct;
  const tone = pct == null ? "bg-muted" : pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-500" : "bg-red-500";
  return (
    <Card className="mb-5 space-y-3 p-4" data-testid="focus-card">
      <div className="flex items-center gap-2 text-sm font-semibold"><Target className="h-4 w-4 text-accent" />Fokusanalys – jobbar jag med mitt uppdrag?</div>
      <Button onClick={run} disabled={busy} size="sm" data-testid="focus-run-btn">
        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />}{busy ? "Analyserar…" : "Kör analys"}
      </Button>
      {res && (
        <div className="space-y-3" data-testid="focus-result">
          <div className="text-xs text-muted-foreground">Jämförs mot: {res.mission}</div>
          {pct != null && (
            <div>
              <div className="mb-1 flex items-center justify-between text-sm"><span>Andel på uppdraget</span><span className="font-head font-bold" data-testid="focus-pct">{pct}%</span></div>
              <div className="h-2.5 overflow-hidden rounded-full bg-secondary"><div className={`h-full ${tone}`} style={{ width: `${pct}%` }} /></div>
            </div>
          )}
          <p className="text-sm">{res.assessment}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />På uppdraget</div>
              <div className="flex flex-wrap gap-1.5">{(res.aligned || []).map((x, i) => <Badge key={i} variant="secondary" data-testid={`focus-aligned-${i}`}>{x}</Badge>)}{!(res.aligned || []).length && <span className="text-xs text-muted-foreground">—</span>}</div>
            </div>
            <div>
              <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-red-700"><AlertTriangle className="h-3.5 w-3.5" />Utanför uppdraget</div>
              <div className="flex flex-wrap gap-1.5">{(res.distractions || []).map((x, i) => <Badge key={i} variant="outline" data-testid={`focus-distract-${i}`}>{x}</Badge>)}{!(res.distractions || []).length && <span className="text-xs text-muted-foreground">—</span>}</div>
            </div>
          </div>
          {res.recommendation && <p className="rounded-lg bg-accent/5 p-3 text-sm italic text-muted-foreground">{res.recommendation}</p>}
        </div>
      )}
    </Card>
  );
}

function MailSuggestions({ onAccepted }) {
  const { data, reload } = useApi("/work/suggestions");
  const list = data || [];
  if (!list.length) return null;
  const accept = async (s) => { try { await api.post(`/work/suggestions/${s.id}/accept`); reload(); onAccepted(); } catch (e) { toast.error(apiErr(e)); } };
  const dismiss = async (s) => { try { await api.post(`/work/suggestions/${s.id}/dismiss`); reload(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <Card className="mb-5 p-0" data-testid="suggestions-card">
      <div className="flex items-center gap-2 border-b p-3 text-sm font-semibold"><Mail className="h-4 w-4 text-accent" />Förslag från dina mejl ({list.length})</div>
      <div className="divide-y">
        {list.map((s) => (
          <div key={s.id} className="flex items-center gap-2 p-3" data-testid={`sugg-${s.id}`}>
            <span className="flex-1 text-sm">{s.title}</span>
            <Button size="sm" variant="ghost" onClick={() => accept(s)} data-testid={`sugg-accept-${s.id}`}><Plus className="mr-1 h-4 w-4" />Lägg till</Button>
            <Button size="icon" variant="ghost" onClick={() => dismiss(s)} data-testid={`sugg-dismiss-${s.id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function MailScanSettings() {
  const { data } = useApi("/work/mail-scan-settings");
  const [enabled, setEnabled] = useState(true);
  const [addresses, setAddresses] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) { setEnabled(data.enabled !== false); setAddresses((data.addresses || []).join(", ")); } }, [data]);
  const save = async () => {
    setSaving(true);
    try {
      const addr = addresses.split(",").map((a) => a.trim()).filter(Boolean);
      await api.put("/work/mail-scan-settings", { enabled, addresses: addr });
      toast.success("Inställning sparad");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <Card className="mb-5 space-y-3 p-4" data-testid="mailscan-settings">
      <div className="flex items-center gap-2 text-sm font-semibold"><Mail className="h-4 w-4 text-accent" />Automatisk mejlskanning (varje morgon)</div>
      <p className="text-xs text-muted-foreground">När er Microsoft 365 är kopplad (med mejl-behörighet) skannas inkorgen varje morgon och nya uppgifter dyker upp som förslag här ovan. Ange valfritt specifika avsändaradresser som mötesägaren vill bevaka.</p>
      <label className="flex items-center gap-2 text-sm"><Switch checked={enabled} onCheckedChange={setEnabled} data-testid="mailscan-enabled" />Aktivera automatisk skanning</label>
      <div>
        <Label>Bevakade avsändaradresser (valfritt, komma-separerat)</Label>
        <Input value={addresses} onChange={(e) => setAddresses(e.target.value)} placeholder="namn@kund.se, projekt@kund.se" data-testid="mailscan-addresses" />
      </div>
      <Button onClick={save} disabled={saving} size="sm" data-testid="mailscan-save"><Save className="mr-1 h-4 w-4" />Spara</Button>
    </Card>
  );
}

function TaskRow({ t, onChanged }) {
  const [snoozing, setSnoozing] = useState(false);
  const [until, setUntil] = useState("");
  const [reason, setReason] = useState("");
  const toggle = async () => { try { await api.patch(`/work/tasks/${t.id}`, { done: !t.done }); onChanged(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async () => { try { await api.delete(`/work/tasks/${t.id}`); onChanged(); } catch (e) { toast.error(apiErr(e)); } };
  const saveSnooze = async () => {
    if (!until) return toast.error("Välj ett datum");
    try { await api.post(`/work/tasks/${t.id}/snooze`, { until, reason }); setSnoozing(false); setReason(""); onChanged(); toast.success("Uppgiften uppskjuten"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const unsnooze = async () => { try { await api.post(`/work/tasks/${t.id}/unsnooze`); onChanged(); } catch (e) { toast.error(apiErr(e)); } };
  const snoozed = t.snoozed_until && t.snoozed_until > new Date().toISOString();
  const srcLabel = t.source === "meeting" ? "möte" : t.source === "mail" ? "mejl" : "ai";
  return (
    <div className="p-3" data-testid={`task-${t.id}`}>
      <div className="flex items-center gap-3">
        <Checkbox checked={t.done} onCheckedChange={toggle} data-testid={`task-check-${t.id}`} />
        <span className={`flex-1 text-sm ${t.done ? "text-muted-foreground line-through" : ""}`}>{t.title}</span>
        {t.source !== "manual" && <Badge variant="outline" className="text-[10px]">{srcLabel}</Badge>}
        {!t.done && t.source === "meeting" && (
          <Button size="icon" variant="ghost" onClick={() => setSnoozing((s) => !s)} data-testid={`task-snooze-btn-${t.id}`}><Clock className="h-4 w-4" /></Button>
        )}
        <Button size="icon" variant="ghost" onClick={del} data-testid={`task-del-${t.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
      </div>
      {snoozed && (
        <div className="mt-1 flex flex-wrap items-center gap-2 pl-8 text-xs text-amber-700" data-testid={`task-snoozed-${t.id}`}>
          <Clock className="h-3.5 w-3.5" />Uppskjuten till {t.snoozed_until.slice(0, 10)}{t.snooze_reason ? ` · ${t.snooze_reason}` : ""}
          <button className="underline" onClick={unsnooze} data-testid={`task-unsnooze-${t.id}`}>Återaktivera</button>
        </div>
      )}
      {snoozing && (
        <div className="mt-2 flex flex-wrap items-end gap-2 pl-8" data-testid={`task-snooze-form-${t.id}`}>
          <div><Label className="text-xs">Skjut upp till</Label><Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="h-9" data-testid={`task-snooze-date-${t.id}`} /></div>
          <div className="min-w-[160px] flex-1"><Label className="text-xs">Anledning</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Varför skjuts den upp?" className="h-9" data-testid={`task-snooze-reason-${t.id}`} /></div>
          <Button size="sm" onClick={saveSnooze} data-testid={`task-snooze-save-${t.id}`}>Spara</Button>
        </div>
      )}
    </div>
  );
}

export default function Struktur() {
  const { data: tasks, reload } = useApi("/work/tasks");
  const [title, setTitle] = useState("");
  const list = tasks || [];
  const open = list.filter((t) => !t.done);
  const done = list.filter((t) => t.done);
  const add = async () => {
    if (!title.trim()) return;
    try { await api.post("/work/tasks", { title }); setTitle(""); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="mx-auto max-w-3xl" data-testid="struktur-page">
      <PageHeader title="Struktur" icon={ListTodo} subtitle="Strukturera ditt arbete med att-göra-listor, hämta uppgifter ur mejl/möten och se om du jobbar med rätt saker." />
      <RoleDescription />
      <MailSuggestions onAccepted={reload} />
      <ImportFromText onAdded={reload} />
      <MailScanSettings />
      <FocusAnalysis />

      <Card className="p-0" data-testid="tasks-card">
        <div className="flex items-center gap-2 border-b p-3">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            placeholder="Lägg till en uppgift…" data-testid="task-input" />
          <Button onClick={add} data-testid="task-add-btn"><Plus className="mr-1 h-4 w-4" />Lägg till</Button>
        </div>
        {list.length === 0 ? (
          <EmptyState icon={ListTodo} title="Inga uppgifter än" text="Lägg till manuellt eller hämta ur ett mejl/möte." />
        ) : (
          <>
            <div className="divide-y">{open.map((t) => <TaskRow key={t.id} t={t} onChanged={reload} />)}</div>
            {done.length > 0 && (
              <div className="border-t">
                <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Klart ({done.length})</div>
                <div className="divide-y opacity-70">{done.map((t) => <TaskRow key={t.id} t={t} onChanged={reload} />)}</div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
